import { put } from "redux-saga/effects";

/**
 * Uplifted from `redux-store/saga/itemStatus` (apps/web 87 lines, apps/mobile 83).
 *
 * A SPLIT. Two of the four workers are byte-identical (modulo quote style) and live here;
 * the other two diverge, and so do the endpoint arities they call, so they belong to the
 * platform files along with each `root`.
 *
 * | worker | shape |
 * |---|---|
 * | `prepareItemsFun` | identical -> here |
 * | `refreshItemsFun` | identical -> here |
 * | `getInStockItemsData` | DIVERGENT — one platform passes a THIRD endpoint argument |
 * | `getSoldOutItemsData` | FULLY DIVERGENT — see both variants; different gate, different
 *   payload shape, and web dispatches a second action mobile has no counterpart for |
 *
 * `root` is NOT here even though both apps watch the same four actions in the same order: it
 * binds the platform's own workers, so a shared `root` would have to reach back into the
 * variants. Same treatment as `saga/discount` and `saga/printer`.
 *
 * ⚠️ This file is bundled on BOTH platforms, so it must not name either platform's exclusive
 * symbols outside of prose (the session-21 comment trap). The bare folder imports below are
 * REQUIRED here (hazard 16) — `utils/apiService` and `config/endpoints` are split folders and
 * this file runs on both platforms.
 */
import api from "../../../utils/apiService";
import { ENDPOINT } from "../../../config/endpoints";
import { prepareItemsSuccess, refreshItemsSuccess } from "../../slices/ItemStatusSlice";

/**
 * POST the prepare-items payload and forward the response verbatim.
 *
 * ⚠️ The bare `catch` that only `console.log`s is the apps' own — a failure dispatches nothing
 * and the caller is never told. Preserved.
 */
export function* prepareItemsFun(action: any): Generator<any, void, any> {
  try {
    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.ITEMSTATUS.prepareItems(),
      data: action.payload,
    });
    yield put(prepareItemsSuccess(response));
  } catch (error) {
    console.log("error", error);
  }
}

/**
 * POST the refresh-items payload.
 *
 * ⚠️ **The response is REPLACED with `{ status: "success" }` whenever it carries no `code`.**
 * That is the apps' own behaviour, in both of them, and it means a successful refresh never
 * forwards the server's actual body. Preserved verbatim.
 */
export function* refreshItemsFun(action: any): Generator<any, void, any> {
  try {
    let response = yield api.request({
      method: "POST",
      url: ENDPOINT.ITEMSTATUS.refreshItems(),
      data: action.payload,
    });
    if (!response?.code) {
      response = {
        status: "success",
      };
    }
    yield put(refreshItemsSuccess(response));
  } catch (error) {
    console.log("error", error);
  }
}
